import React from "react";

import { useQuery } from "@apollo/client";
import { Box, useTheme, Theme, Typography } from "@mui/material";
import { endOfWeek, startOfWeek, getDate, parseISO, format } from "date-fns";
import { useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { VictoryLine, VictoryScatter, VictoryAxis } from "victory";
import { VictoryChart } from "victory-chart";

import { graphql } from "@/gql";

import { WeightChartData } from "../types/WeightChartData";
import { getLineData } from "../utils/getLineData";
import { getTickValues } from "../utils/getTickValues";
import { getWeeklyDates } from "../utils/getWeeklyDates";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
  },
  chart: {
    flexGrow: 1,
  },
  header: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(0, 2),
  },
  goal: {
    display: "flex",
    alignItems: "center",
  },
  tag: {
    padding: theme.spacing(2, 4),
    background: "#FFF6EB",
    color: theme.palette.warning.main,
    borderRadius: 24,
  },
  noSpace: {
    padding: 0,
    margin: 0,
  },
}));

interface Props {
  targetWeight: number | null | undefined;
  currentWeight: number | null | undefined;
  loading: boolean;
}

const weightChartQuery = graphql(`
  query WeightChart(
    $startDate: ISO8601Date!
    $endDate: ISO8601Date!
    $clientId: ID!
  ) {
    dailyWeightByDateRange(
      startDate: $startDate
      endDate: $endDate
      clientId: $clientId
    ) {
      id
      date
      value
    }
  }
`);

const WeightChart: React.FC<Props> = ({
  targetWeight,
  currentWeight,
  loading,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();
  const { clientId, date } = useParams<{ clientId: string; date: string }>();
  const start = startOfWeek(parseISO(date || new Date().toDateString()));
  const end = endOfWeek(parseISO(date || new Date().toDateString()));

  // Create an array of dates for the label
  const dates = getWeeklyDates(start, end);

  const { data } = useQuery(weightChartQuery, {
    variables: {
      clientId: clientId ?? "",
      startDate: format(start, "yyyy-MM-dd"),
      endDate: format(end, "yyyy-MM-dd"),
    },
  });
  const logs = data?.dailyWeightByDateRange ?? [];

  // Sanitize the data for the Scatter graph
  const chartData: WeightChartData[] = dates.map((date) => {
    const weightLog = logs?.find(
      (log) => log.date === format(date, "yyyy-MM-dd")
    );
    if (weightLog) {
      return {
        date: getDate(date).toString(),
        weight: weightLog.value ?? 0,
      };
    } else {
      return {
        date: getDate(date).toString(),
        weight: null,
      };
    }
  });

  // Create the data for the line graph putting fillers on null values
  // so the dots will connect if there are null values in between
  const lineChartData = getLineData(chartData, currentWeight) ?? [];

  // Compute the ticks for the graph
  const yTickValues = getTickValues(lineChartData);
  const hasYtickValues = yTickValues.length !== 0;
  if (loading) return null;
  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography
          variant="body1"
          fontWeight="bold"
          className={classes.noSpace}
        >{`${format(start, "dd MMM")} - ${format(end, "dd MMM")}`}</Typography>
        <div className={classes.goal}>
          <Typography variant="subtitle1" color="textSecondary">
            Goal Weight
          </Typography>
          <Box marginRight={theme.spacing(0.5)} />
          <span className={classes.tag}>{`${targetWeight ?? 0} kg`}</span>
        </div>
      </div>
      <div className={classes.chart}>
        <VictoryChart
          polar={false}
          height={324}
          style={{
            parent: { margin: 3 },
          }}
        >
          <VictoryLine
            interpolation="linear"
            data={lineChartData}
            style={{
              data: {
                stroke: theme.palette.warning.main,
              },
            }}
            x="date"
            y="weight"
            domain={
              hasYtickValues
                ? {
                    y: [yTickValues[0], yTickValues[yTickValues.length - 1]],
                  }
                : undefined
            }
          />
          <VictoryScatter
            data={chartData}
            size={8}
            style={{
              data: {
                fill: theme.palette.common.white,
                stroke: theme.palette.warning.main,
                strokeWidth: 3,
              },
            }}
            x="date"
            y="weight"
            domain={
              hasYtickValues
                ? {
                    y: [yTickValues[0], yTickValues[yTickValues.length - 1]],
                  }
                : undefined
            }
          />
          <VictoryAxis
            tickFormat={(t) => Math.floor(t)}
            dependentAxis
            style={{
              axis: { stroke: "transparent" },
              grid: { stroke: theme.palette.grey["300"], strokeDasharray: 8 },
              ticks: { stroke: "none" },
              tickLabels: {
                padding: 20,
                fontSize: "14px",
                fontFamily: "Mulish",
                fontWeight: 600,
                opacity: 0.7,
              },
            }}
          />
          <VictoryAxis
            standalone
            label="Dates"
            style={{
              axis: { stroke: "#D4DBE5", opacity: 0.7 },
              axisLabel: {
                fontSize: "14px",
                fontFamily: "Mulish",
                fontWeight: 700,
                fill: "#4C515A",
                opacity: 0.7,
                padding: 34,
              },
              tickLabels: {
                fontSize: "14px",
                padding: 10,
                fontFamily: "Mulish",
                fontWeight: 600,
                fill: "#4C515A",
                opacity: 0.7,
              },
            }}
          />
        </VictoryChart>
      </div>
    </div>
  );
};

export default WeightChart;
